/**
 * ProvisionTimingCard — how long a sandbox's most recent provision/rebuild took.
 *
 * Renders a timeline (Gantt) when the run recorded per-step `startedAt` anchors,
 * so parallelism is visible — container builds overlapping inside "Start app".
 * Older runs without those anchors fall back to a stacked-bar breakdown that
 * needs only durations. Both reconstruct the same logical hierarchy stored flat
 * on `sandbox_provision_timings` (see {@link ./provisionTimingWaterfall} and
 * {@link ./ProvisionTimingBars}).
 *
 * @module features/sandboxes/components/overview/ProvisionTimingCard
 */
import React, { useMemo } from 'react';
import type { ProvisionTimingSummary } from '@chatsdet/types';
import { formatDurationLabel } from '@chatsdet/ui';
import type { ProvisionTiming } from '../../hooks/useProvisionTimings';
import { ProvisionTimingBars } from './ProvisionTimingBars';
import { ProvisionTimingSummaryBars } from './ProvisionTimingSummaryBars';
import { ProvisionTimingTimeline } from './ProvisionTimingTimeline';
import { buildProvisionWaterfall } from './provisionTimingWaterfall';

/**
 * Where the box came from, superadmin-only. `warm_pool` covers both the base
 * pool and — on runs recorded before `stopped_pool` existed — a stopped-pool
 * claim.
 */
const LAUNCH_SOURCE_LABELS: Record<string, string> = {
  stopped_pool: 'stopped pool',
  warm_pool: 'warm pool',
  cold_fresh: 'cold launch',
};

export interface ProvisionTimingCardProps {
  /** Per-step rows. Superadmin-only — the API sends `summary` to everyone else. */
  timings: ProvisionTiming[] | null;
  /** Ready/build/total roll-up sent to non-superadmins. */
  summary?: ProvisionTimingSummary | null;
  /**
   * When provisioning failed, the `<phase> · <step>` label of the failing step.
   * Rendered as a red caption so the timeline (which shows how far provisioning
   * got before it died) is annotated with where it stopped.
   */
  failedStep?: string | null;
}

/**
 * Flat provision-timing body — a small meta line above the timeline (or stacked
 * bars). The section heading is provided by the caller (`SettingsSection`), so
 * this renders no card chrome of its own.
 *
 * Which view renders is decided by what the API sent, not by a local permission
 * check: per-step `timings` mean a superadmin, a `summary` means everyone else.
 */
export function ProvisionTimingCard({ timings, summary, failedStep }: ProvisionTimingCardProps) {
  const waterfall = useMemo(
    () => (timings ? buildProvisionWaterfall(timings, { failedStep }) : null),
    [timings, failedStep]
  );

  // Non-superadmin: two bars, a total, and whether the machine was already
  // running. `warmStart` is null on runs predating the launch source — unknown,
  // so no badge rather than a wrong one.
  if (!timings || timings.length === 0) {
    if (!summary) return null;
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          {summary.warmStart !== null && (
            <span className="text-xs rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
              {summary.warmStart ? 'warm start' : 'cold start'}
            </span>
          )}
          <span className="text-xs font-mono text-muted-foreground">
            total {formatDurationLabel(summary.totalMs)}
          </span>
        </div>
        <ProvisionTimingSummaryBars summary={summary} />
        {failedStep && <FailedStepCaption failedStep={failedStep} />}
      </div>
    );
  }

  const total = timings.find((t) => t.kind === 'phase' && t.step === 'total');
  const warmCold = timings.find((t) => t.warmCold)?.warmCold ?? null;

  return (
    <div>
      {(warmCold || total) && (
        <div className="flex items-center gap-2 mb-2">
          {warmCold && (
            <span className="text-xs rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
              {LAUNCH_SOURCE_LABELS[warmCold] ?? warmCold}
            </span>
          )}
          {total && (
            <span className="text-xs font-mono text-muted-foreground">
              total {formatDurationLabel(total.durationMs)}
            </span>
          )}
        </div>
      )}
      {waterfall ? (
        <ProvisionTimingTimeline waterfall={waterfall} />
      ) : (
        <ProvisionTimingBars timings={timings} />
      )}
      {failedStep && <FailedStepCaption failedStep={failedStep} />}
    </div>
  );
}

function FailedStepCaption({ failedStep }: { failedStep: string }) {
  return (
    <div className="mt-2 flex items-center gap-2">
      <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-red-500" />
      <span className="text-xs text-red-700">
        Provisioning failed at <span className="font-mono">{failedStep}</span> — steps after this
        did not run.
      </span>
    </div>
  );
}

ProvisionTimingCard.displayName = 'ProvisionTimingCard';
